import get from "lodash/get";
import React, {Component} from "react";
import {graphql} from "react-apollo";
import compose from "lodash/flowRight";
import {withRouter} from "react-router-dom";
import {withTranslation} from "@unibuddy/intl";
import {VisuallyHidden} from "@unibuddy/patron";

import {getParameterByName} from "shared/Utils/UrlUtils";
import withStartChatHOC from "shared/Chat/hocs/withStartChat/withStartChat";
import withAnalytics from "shared/AnalyticsNew/withAnalytics/withAnalytics";
import withAuth from "shared/Auth/withAuth/withAuth";
import Queue, {QueueItem} from "shared/General/components/Queue/Queue";
import EmptyState from "shared/General/components/EmptyState/EmptyState";
import withEmbeddedEvents from "shared/EmbeddedEvents/withEmbeddedEvents/withEmbeddedEvents";
import {DOES_NOT_EXIST} from "shared/Utils/ErrorConstantsUtils";
import {isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity} from "shared/Utils/FeatureUtils";
import {HAS_UNIVERSITY_FEATURE_QUERY} from "shared/Feature/HasUniversityFeature";
import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import {PRESIGNUP_MESSAGE_KEY} from "shared/constants";

import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import {WideContainer} from "../../../../../components/WideContainer/WideContainer";
import DelinkedMessage from "../../components/DelinkedMessageBox/DelinkedMessage";
import EmptySuggestionsIcon from "../../components/EmptySuggestionsIcon/EmptySuggestionsIcon";
import withChatInput from "../../components/ChatInputProvider/withChatInput";
import {Chat as ChatV2} from "../../components/Chat/Chat";
import withAskHasApplied from "../../../Applicant/hoc/withAskHasApplied/withAskHasApplied";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import s from "../Chat/Chat.pcss";
import {PopupQuestions} from "../../components/PopupQuestions/PopupQuestions";
import {getMentorInboxInfoOne} from "../../utils/InboxInfo";
import {StyledChatComponent} from "../common/StyledChatComponent";
import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_CONVERSATION_WITH} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import {INTL_INBOX} from "../../../../../translations/inbox";

export class StartChatComponent extends Component {
    componentDidMount() {
        this.props.analytics.events.pageViewed({prefix: this.props.analytics.pageNames.startChat});

        const opponentId = get(this.props, "match.params.opponentId");
        const preSignUpMessage = window.localStorage.getItem(PRESIGNUP_MESSAGE_KEY(opponentId));

        if (preSignUpMessage) {
            try {
                const parsedMessage = JSON.parse(preSignUpMessage);
                this.onSend({text: parsedMessage.text});
            } catch (e) {
                console.error("Error parsing preSignUpMessage", e);
            }
            window.localStorage.removeItem(`preSignUpMessage-${opponentId}`);
        }
    }

    onSend = ({text}) => {
        // Record analytics events for starting conversation and sending message
        let ubSource = getParameterByName(this.props.location.search, "ub_source");
        ubSource = ubSource ? ubSource.split(" - ") : [];
        const buddyPosition =
            getParameterByName(this.props.location.search, "buddyPosition") || ubSource[0];
        const buddyGroupType =
            getParameterByName(this.props.location.search, "buddyGroupType") || ubSource[1];
        const isFirstRealMessage = get(this.props, "messages.length") <= 1;

        let opponentType = buddyGroupType || "mentor";
        if (
            opponentType === "mentor" &&
            get(this.props, "introMessage.sender.degreeLevel") === "Alumni"
        ) {
            opponentType = "alumni";
        }

        if (isFirstRealMessage)
            this.props.analytics.events.conversationStarted({
                buddyPosition,
                opponentId: get(this.props, "match.params.opponentId"),
                opponentType,
            });

        this.props.embeddedEvents.messageSent();

        this.props.onSendMessage(text);
    };

    render() {
        const name = get(this.props, "introMessage.sender.firstName", "Buddy");
        const loading = !this.props.introMessage;
        const showAppliedToUniversityQuestion =
            !isFeatureEnabledByUnibuddyOrByUnibuddyAndUniversity(
                this.props.features,
                "hideAppliedToUniversityQuestion",
            );

        const {t} = this.props;
        const queue = !loading && showAppliedToUniversityQuestion && (
            <Queue>
                <QueueItem enabled={!get(this.props.askHasApplied, "complete", true)}>
                    {({isOpen, onDismiss}) => {
                        return (
                            <PopupQuestions
                                askHasApplied={this.props.askHasApplied}
                                onDismiss={onDismiss}
                                isOpen={isOpen}
                            />
                        );
                    }}
                </QueueItem>
            </Queue>
        );

        if (
            get(this.props.chatGroupQueryError, "graphQLErrors[0].extensions.code") ===
            DOES_NOT_EXIST
        ) {
            return (
                <EmptyState
                    title={t("profileNotFound", "Profile not found")}
                    text={t(
                        "profileNotFoundDescription",
                        "Sorry, it appears that this profile is not available.",
                    )}
                    image={<EmptySuggestionsIcon />}
                />
            );
        }

        if (loading) return <LoadingChat />;

        const redirectToRelink = () => {
            // redirecting to 2nd stage signup on settings tab
            this.props.history.replace(this.props.location.pathname, {from: "delinked"});
        };

        if (!this.props.loadingHasUniversityFeature) {
            const universitySlug = get(this.props, "universitySlug", "");
            const meAuthState = get(this.props, "authState.me");
            const isDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);

            const chatGroup = {
                opponent: {
                    ...this.props.introMessage.sender,
                    inboxInfoOne:
                        getMentorInboxInfoOne({
                            sender: get(this.props, "introMessage.sender", {}),
                            university: get(this.props, "university", {}),
                            t,
                        }) ||
                        get(this.props, "introMessage.sender.degree.name") ||
                        get(this.props, "introMessage.sender.staffFields.department"),
                    inboxInfoTwo: get(this.props, "introMessage.sender.university.name"),
                    isEnterprise: get(this.props, "introMessage.sender.university.isEnterprise"),
                },
                delinked: isDelinked,
            };

            return (
                <div id="chat-page">
                    <PageTitle
                        title={this.props.t("conversationWith", `Conversation with ${name}`, {
                            name,
                        })}
                    />
                    <VisuallyHidden>
                        <PageHeader
                            title={this.props.t(
                                `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                                INTL_INBOX.defaultValue,
                            )}
                        />
                    </VisuallyHidden>
                    <div className={s.container}>
                        <StyledChatComponent className={s.chat}>
                            <ChatV2
                                messages={this.props.messages}
                                user={this.props.me.anyUser}
                                chatGroup={chatGroup}
                                chatFooterHidden={isDelinked}
                                chatPopup={
                                    isDelinked ? (
                                        <DelinkedMessage redirectToRelink={redirectToRelink} />
                                    ) : null
                                }
                                university={get(this.props, "university", {})}
                                onSend={this.onSend}
                                queue={queue}
                            />
                        </StyledChatComponent>
                    </div>
                </div>
            );
        }
        return (
            <div id="chat-page">
                <PageTitle
                    title={this.props.t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {
                            name,
                        },
                    )}
                />
                <WideContainer>
                    <VisuallyHidden>
                        <PageHeader
                            title={this.props.t(
                                `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                                INTL_INBOX.defaultValue,
                            )}
                        />
                    </VisuallyHidden>
                    <div className={s.container}>
                        <div className={s.chat}>
                            <LoadingChat />
                        </div>
                    </div>
                </WideContainer>
            </div>
        );
    }
}

const HAS_UNIVERSITY_FEATURE_QUERY_OPTIONS = {
    name: "hasUniversityFeature",
    options(props) {
        return {
            variables: {
                feature: "hideAppliedToUniversityQuestion",
                universitySlug: get(props, "universitySlug", ""),
            },
            fetchPolicy: "network-only",
        };
    },
    props({hasUniversityFeature: {university, loading}}) {
        return {
            features: get(university, "config.features", []),
            loadingHasUniversityFeature: loading,
        };
    },
};

export default compose(
    withRouter,
    withAuth,
    withAnalytics,
    withStartChatHOC("jigglypuff"),
    withAskHasApplied,
    withEmbeddedEvents,
    withChatInput,
    withTranslation([IntlNamespaces.CHAT, IntlNamespaces.INBOX]),
    graphql(HAS_UNIVERSITY_FEATURE_QUERY, HAS_UNIVERSITY_FEATURE_QUERY_OPTIONS),
)(StartChatComponent);
